import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Table,
  Button,
  Spin,
  Descriptions,
  Progress,
  Tabs,
  Timeline,
  Empty,
  Input,
  Modal,
  Form,
  DatePicker,
  Space,
  Select,
  message,
  InputNumber,
  Alert,
  Badge,
  Switch,
} from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  ScheduleOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  TransactionOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import moment from "moment";
import InvestmentStatementGenerator from "../components/investmentStatementGenerator";
import {
  calculateAccruedValue,
  calculateMaturityAmounts,
  toNumber,
} from "../utils/investmentCalculations";
import { TransactionHooks } from "../utils/transactionHooks";
import { irr, xirr, xnpv, toPercent } from "../utils/investmentMetrics";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const INVESTMENT_STATUS = {
  Active: { color: "#52c41a", text: "Active" },
  Matured: { color: "#1890ff", text: "Matured" },
  Withdrawn: { color: "#f5222d", text: "Withdrawn" },
  Pending: { color: "#faad14", text: "Pending" },
};

const InvestmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [investment, setInvestment] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [autoRenewal, setAutoRenewal] = useState(
    investment?.auto_renewal || false
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showReinvestModal, setShowReinvestModal] = useState(false);
  const [discountRate, setDiscountRate] = useState(10);
  const { user, company, accounts, updateAccountBalance } = useContext(AppContext);
  const [form] = Form.useForm();
  const [reinvestForm] = Form.useForm();
  const token = localStorage.getItem("token");
  const paymentMethods = [
    { label: "Mobile Money", value: "mobile_money" },
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "Cash", value: "cash" },
  ];

  const DEFAULT_WITHHOLDING_TAX = 15;

  useEffect(() => {
    const fetchInvestmentDetails = async () => {
      try {
        const [investmentRes, transactionsRes] = await Promise.all([
          axios.get(
            `https://zedlenders.pickmesms.com/items/investments/${id}?fields=*,investor_id.*,investment_product.*`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/items/transactions?filter[investment_id]=${id}&filter[company]=${company?.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
        ]);

        setInvestment(investmentRes.data.data);
        setTransactions(transactionsRes.data.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestmentDetails();
  }, [id]);

  const calculateProjectedReturns = () => {
    if (!investment) return 0;
    const interestType =
      investment.interest_type ||
      investment.investment_product?.interest_type ||
      "simple";
    const compoundFrequency =
      investment.compound_frequency ||
      investment.investment_product?.compound_frequency ||
      "monthly";
    return calculateAccruedValue({
      principal: investment.principal_amount,
      annualRatePercent: investment.interest_rate,
      startDate: investment.investment_date,
      interestType,
      compoundFrequency,
    });
  };

  const buildCashflows = () => {
    if (!investment) return [];
    const flows = [];
    flows.push({
      date: investment.investment_date,
      amount: -toNumber(investment.principal_amount),
    });
    transactions.forEach((txn) => {
      const type = (txn.transaction_type || "").toLowerCase();
      if (type.includes("investment payout")) {
        flows.push({
          date: txn.transaction_date,
          amount: toNumber(txn.amount),
        });
      } else if (type.includes("investment deposit")) {
        flows.push({
          date: txn.transaction_date,
          amount: -toNumber(txn.amount),
        });
      }
    });
    return flows.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const cashflows = buildCashflows();
  const irrValue = irr(cashflows);
  const xirrValue = xirr(cashflows);
  const npvValue = xnpv(toNumber(discountRate) / 100, cashflows);

  const handlePayout = async (values) => {
    const account = accounts?.find((acc) => acc.id === values.account_id);
    if (!account) {
      message.error("Please select a valid payout account");
      return;
    }

    const payoutGross = toNumber(values.amount);
    const accruedValue = calculateProjectedReturns();
    const accruedInterest = Math.max(0, accruedValue - toNumber(investment?.principal_amount));
    const interestPortion = Math.min(payoutGross, accruedInterest);
    const principalPortion = Math.max(0, payoutGross - interestPortion);
    const taxRate =
      toNumber(
        investment?.withholding_tax_rate ||
          investment?.investment_product?.withholding_tax_rate ||
          DEFAULT_WITHHOLDING_TAX
      ) / 100;
    const withholdingTax = interestPortion * taxRate;
    const cashOut = payoutGross - withholdingTax;

    const newAccountBalance = toNumber(account.balance) - cashOut;
    const investmentBalance = toNumber(investment?.total_maturity_amount) - payoutGross;
    const netInvestmentBalance = toNumber(investment?.net_maturity_amount) - cashOut;

    if (newAccountBalance < 0) {
      message.error("Insufficient balance in payout account");
      return;
    }

    try {
      const payload = {
        ...values,
        user: user.id,
        investment_id: id,
        transaction_date: moment().format("YYYY-MM-DD"),
        transaction_type: "Investment Payout",
        amount: cashOut,
        is_loan_transaction: false,
        transaction_status: "Completed",
        company: company.id,
        notes: values.notes || `Payout gross ${payoutGross} with tax ${withholdingTax.toFixed(2)}`,
      };

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/transactions",
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      message.success("Payout processed successfully");
      setShowPayoutModal(false);
      form.resetFields();
      // Update local state
      const newPayment = response.data.data;
      setTransactions([newPayment, ...transactions]);

      const newStatus = investmentBalance <= 0 ? "Withdrawn" : "Active";

      setInvestment({
        ...investment,
        status: newStatus,
        total_maturity_amount: investmentBalance,
        net_maturity_amount: netInvestmentBalance,
      });

      // Update account balance
      updateAccountBalance(values.account_id, newAccountBalance);
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${values.account_id}`,
        { balance: newAccountBalance },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await TransactionHooks.onInvestmentPayout({
        investmentId: id,
        principalAmount: principalPortion,
        interestAmount: interestPortion,
        withholdingTaxAmount: withholdingTax,
        date: new Date(),
        company: company.id,
        token,
        accountId: values.account_id,
      });

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/investments/${id}`,
        {
          status: newStatus,
          total_maturity_amount: investmentBalance,
          net_maturity_amount: netInvestmentBalance,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      message.error("Failed to process payout: " + err.message);
    }
  };

  const handleReinvestment = async (values) => {
    try {
      const newTerm = values.term_days;
      const interestRate = toNumber(values.interest_rate || investment?.interest_rate);
      const interestType =
        values.interest_type ||
        investment?.interest_type ||
        investment?.investment_product?.interest_type ||
        "simple";
      const compoundFrequency =
        values.compound_frequency ||
        investment?.compound_frequency ||
        investment?.investment_product?.compound_frequency ||
        "monthly";
      const withholdingTaxRate = toNumber(
        investment?.withholding_tax_rate ||
          investment?.investment_product?.withholding_tax_rate ||
          DEFAULT_WITHHOLDING_TAX
      );
      const maturity = calculateMaturityAmounts({
        principal: toNumber(investment?.principal_amount),
        annualRatePercent: interestRate,
        days: newTerm,
        interestType,
        compoundFrequency,
        withholdingTaxRate,
      });

      const newInvestment = {
        ...investment,
        term_days: newTerm,
        interest_rate: interestRate,
        interest_type: interestType,
        compound_frequency: compoundFrequency,
        maturity_date: moment().add(newTerm, "days").format("YYYY-MM-DD"),
        expected_interest: maturity.expectedInterest,
        total_maturity_amount: maturity.grossMaturity,
        net_maturity_amount: maturity.netMaturity,
        status: "active",
      };

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/investments/${id}`,
        newInvestment,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInvestment(newInvestment);
      message.success("Investment reinvested successfully");
      setShowReinvestModal(false);
    } catch (err) {
      message.error("Reinvestment failed: " + err.message);
    }
  };

  const maturityCountdown = () => {
    if (!investment) return null;
    const daysLeft = moment(investment.maturity_date).diff(moment(), "days");
    return (
      <Badge
        count={`${daysLeft} DAYS TO MATURITY`}
        style={{
          backgroundColor: daysLeft > 7 ? "#1890ff" : "#f5222d",
          padding: "0 8px",
          borderRadius: 4,
          fontWeight: 500,
        }}
      />
    );
  };

  const onToggleAutoRenewal = async (checked) => {
    try {
      setLoading(true);
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/investments/${investment.id}`,
        {
          auto_renewal: checked,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAutoRenewal(checked);
      message.success("Auto-renewal updated successfully");
    } catch (error) {
      message.error("Failed to update auto-renewal");
      console.error("Error updating auto-renewal:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="center-loader">
        <Spin indicator={<ReloadOutlined spin />} size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Investment Loading Error"
        description={error.message}
        showIcon
        action={
          <Button type="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back to Investments
      </Button>

      <Card bordered={false}>
        {/* Header Section */}
        <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
          <Col flex="auto">
            <Title level={3} style={{ marginBottom: 4 }}>
              {investment.investor_id.first_name +
                " " +
                investment.investor_id.last_name}
              <Text type="secondary" style={{ marginLeft: 16 }}>
                {investment.investor_id.nrc}
              </Text>
            </Title>
            <Space>
              <Tag color={INVESTMENT_STATUS[investment?.status || 'Pending']?.color || 'default'}>
                {INVESTMENT_STATUS[investment?.status || 'Pending']?.text || 'Unknown'}
              </Tag>
              {maturityCountdown()}
            </Space>
          </Col>
          <Col>
            <Space>
              <Text strong style={{ marginRight: 8 }}>
                Auto Renewal:
              </Text>
              <Switch
                checked={autoRenewal}
                onChange={onToggleAutoRenewal}
                loading={loading}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Col>
        </Row>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Principal Amount"
                value={investment.principal_amount}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#1890ff" }}
                suffix="ZMW"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Current Accrued Value"
                value={Number(calculateProjectedReturns()).toFixed(2)}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#3f8600" }}
                suffix="ZMW"
              />
              {/* <Progress
                percent={Math.round(investment.total_maturity_amount)}
                status="active"
                style={{ marginTop: 16 }}
              /> */}
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Gross Maturity Value"
                value={Number(investment.total_maturity_amount).toFixed(2)}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#1890ff" }}
                suffix="ZMW"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Investment Term"
                value={`${investment.term_days} days`}
                prefix={<ScheduleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Tabs Section */}
        <Tabs defaultActiveKey="1">
          <TabPane tab="Investment Overview" key="1">
            <Row gutter={24}>
              <Col span={16}>
                <Card title="Transaction History">
                  <Timeline mode="alternate">
                    {transactions.map((txn) => (
                      <Timeline.Item
                        key={txn.id}
                        color={
                          txn.transaction_type?.toLowerCase().includes("payout")
                            ? "green"
                            : "blue"
                        }
                        label={moment(txn.transaction_date).format(
                          "DD MMM YYYY"
                        )}
                      >
                        <div className="transaction-card">
                          <Text strong style={{ fontSize: 16 }}>
                            ZMW {txn.amount.toLocaleString()}
                          </Text>
                          <div style={{ marginTop: 8 }}>
                            <Tag
                              color={
                                txn?.transaction_status === "Completed"
                                  ? "green"
                                  : "orange"
                              }
                            >
                              {txn?.transaction_status || "Unknown"}
                            </Tag>
                            <Text type="secondary">{txn.transaction_type}</Text>
                          </div>
                          {txn.notes && (
                            <Text
                              type="secondary"
                              style={{ marginTop: 8, display: "block" }}
                            >
                              {txn.notes}
                            </Text>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Card>
              </Col>
              <Col span={8}>
                {
                  <Card title="Quick Actions" style={{ marginBottom: 24 }}>
                    <Button
                      block
                      type="primary"
                      disabled={investment?.status === "Withdrawn"}
                      onClick={() => setShowPayoutModal(true)}
                      style={{ marginBottom: 8 }}
                    >
                      Record Payout
                    </Button>
                    <Button
                      block
                      disabled={investment.status === "Withdrawn"}
                      onClick={() => setShowReinvestModal(true)}
                      style={{ marginBottom: 8 }}
                    >
                      Reinvest Capital
                    </Button>
                    <InvestmentStatementGenerator investment={investment} />
                  </Card>
                }

                <Card title="Performance Summary">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Annualized Yield">
                      {investment.interest_rate}%
                    </Descriptions.Item>
                    <Descriptions.Item label="Days Held">
                      {moment().diff(investment.investment_date, "days")}
                    </Descriptions.Item>
                    <Descriptions.Item label="Risk Category">
                      <Tag color="#52c41a">Low Risk</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card title="Investment Analytics" style={{ marginTop: 16 }}>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text type="secondary">IRR</Text>
                      <div>
                        <Text strong>
                          {toPercent(irrValue)?.toFixed(2) || "N/A"}%
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">XIRR</Text>
                      <div>
                        <Text strong>
                          {toPercent(xirrValue)?.toFixed(2) || "N/A"}%
                        </Text>
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={12} style={{ marginTop: 12 }}>
                    <Col span={12}>
                      <Text type="secondary">Discount Rate (%)</Text>
                      <InputNumber
                        min={0}
                        max={100}
                        value={discountRate}
                        onChange={(value) => setDiscountRate(value)}
                        style={{ width: "100%" }}
                      />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">NPV</Text>
                      <div>
                        <Text strong>
                          ZMW {Number.isFinite(npvValue) ? npvValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "N/A"}
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Documents & Compliance" key="2">
            <Card
              title="Legal Documents"
              extra={
                <Button icon={<FileAddOutlined />}>Upload Document</Button>
              }
            >
              {/* Document management component */}
            </Card>
          </TabPane>

          <TabPane tab="Reinvestment History" key="3">
            <Card title="Reinvestment Timeline">
              {/* Reinvestment history component */}
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* Payout Modal */}
      <Modal
        title="Process Payout"
        visible={showPayoutModal}
        onCancel={() => setShowPayoutModal(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handlePayout} layout="vertical">
          <Form.Item
            label="Gross Payout Amount (ZMW)"
            name="amount"
            rules={[{ required: true }]}
          >
            <InputNumber
              min={0}
              max={calculateProjectedReturns()}
              style={{ width: "100%" }}
              formatter={(value) =>
                `ZMW ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/ZMW\s?|(,*)/g, "")}
            />
          </Form.Item>
          <Form.Item
            label="Payout Account"
            name="account_id"
            rules={[{ required: true, message: "Please select payout account" }]}
          >
            <Select
              options={(accounts || []).map((acc) => ({
                value: acc.id,
                label: `${acc.name || acc.account_name} (Balance: ZMW ${toNumber(acc.balance).toLocaleString()})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Payout Method"
            name="payment_method"
            rules={[
              { required: true, message: "Please select payment method" },
            ]}
          >
            <Select options={paymentMethods} />
          </Form.Item>
          <Form.Item
            label="Reference Number"
            name="reference_number"
            rules={[
              {
                required: true,
                message: "Please enter reference number",
              },
            ]}
          >
            <Input
              autoComplete="off"
              placeholder="MMF-12345, BANK-67890, etc."
            />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              rows={3}
              placeholder="Additional payment details..."
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Confirm Payout
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reinvestment Modal */}
      <Modal
        title="Reinvest Capital"
        visible={showReinvestModal}
        onCancel={() => setShowReinvestModal(false)}
        footer={null}
        width={800}
      >
        <Form form={reinvestForm} onFinish={handleReinvestment} layout="vertical">
          <Form.Item
            label="New Term (days)"
            name="term_days"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Interest Rate (%)" name="interest_rate">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Interest Type" name="interest_type">
                <Select
                  options={[
                    { label: "Simple", value: "simple" },
                    { label: "Compound", value: "compound" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Compound Frequency" name="compound_frequency">
                <Select
                  options={[
                    { label: "Daily", value: "daily" },
                    { label: "Monthly", value: "monthly" },
                    { label: "Quarterly", value: "quarterly" },
                    { label: "Yearly", value: "yearly" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item shouldUpdate>
            {() => {
              const termDays = toNumber(reinvestForm.getFieldValue("term_days"));
              const interestRate = toNumber(
                reinvestForm.getFieldValue("interest_rate") || investment?.interest_rate
              );
              const interestType =
                reinvestForm.getFieldValue("interest_type") ||
                investment?.interest_type ||
                investment?.investment_product?.interest_type ||
                "simple";
              const compoundFrequency =
                reinvestForm.getFieldValue("compound_frequency") ||
                investment?.compound_frequency ||
                investment?.investment_product?.compound_frequency ||
                "monthly";
              if (!termDays) return null;

              const maturityPreview = calculateMaturityAmounts({
                principal: toNumber(investment?.principal_amount),
                annualRatePercent: interestRate,
                days: termDays,
                interestType,
                compoundFrequency,
                withholdingTaxRate: toNumber(
                  investment?.withholding_tax_rate ||
                    investment?.investment_product?.withholding_tax_rate ||
                    DEFAULT_WITHHOLDING_TAX
                ),
              });

              return (
                <Alert
                  message="Reinvestment Terms"
                  description={`Projected gross maturity: ZMW ${maturityPreview.grossMaturity.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Confirm Reinvestment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvestmentDetailsPage;
